/**
 * One transaction in a list.
 *
 * The amount is shown per posting for a transfer or exchange, because "−30 €"
 * alone would not say where it went. Reversed entries stay visible, struck
 * through and labelled — hiding them would make a balance change with no
 * visible cause.
 */
import { ArrowRight, MoreVertical, Repeat, Undo2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Money, KIND_LABELS, toneForKind } from '@/components/Money';
import { colorClasses } from '@/lib/colors';
import { formatDateRelative, formatRate } from '@/lib/format';
import type { Transaction } from '@/lib/types';

export function TransactionRow({
  tx,
  onVoid,
  showDate = true,
}: {
  tx: Transaction;
  onVoid?: (tx: Transaction) => void;
  showDate?: boolean;
}): JSX.Element {
  const voided = tx.voidedAt != null;
  const isReversal = tx.reversalOfId != null;
  const twoSided = tx.kind === 'TRANSFER' || tx.kind === 'EXCHANGE';

  const outLeg = tx.postings.find((p) => p.amountMinor < 0);
  const inLeg = tx.postings.find((p) => p.amountMinor > 0);
  const single = tx.postings[0];

  const colors = colorClasses(tx.category?.color);

  const title =
    tx.description ||
    tx.category?.name ||
    tx.counterparty?.name ||
    KIND_LABELS[tx.kind];

  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-md border p-3 transition-colors',
        voided && 'opacity-55',
        isReversal && 'border-dashed',
      )}
    >
      <div
        className={cn(
          'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full',
          twoSided ? 'bg-muted' : colors.soft,
        )}
      >
        {twoSided ? (
          <Repeat className="h-4 w-4 text-muted-foreground" />
        ) : (
          <span className={cn('text-xs font-semibold', colors.text)}>
            {(tx.category?.name ?? title).slice(0, 1).toUpperCase()}
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className={cn('truncate font-medium', voided && 'line-through')}>{title}</span>
          {voided ? (
            <Badge variant="neutral" className="text-[10px]">
              сторнирана
            </Badge>
          ) : null}
          {isReversal ? (
            <Badge variant="neutral" className="text-[10px]">
              сторно
            </Badge>
          ) : null}
          {tx.loanId ? (
            <Badge variant="secondary" className="text-[10px]">
              заем
            </Badge>
          ) : null}
        </div>

        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          {showDate ? <span>{formatDateRelative(tx.date)}</span> : null}

          {twoSided && outLeg?.account && inLeg?.account ? (
            <span className="flex items-center gap-1">
              <span className="truncate">
                {outLeg.account.fund?.name} · {outLeg.account.location?.name}
              </span>
              <ArrowRight className="h-3 w-3 shrink-0" />
              <span className="truncate">
                {inLeg.account.fund?.name} · {inLeg.account.location?.name}
              </span>
            </span>
          ) : single?.account ? (
            <span className="truncate">
              {single.account.fund?.name} · {single.account.location?.name}
            </span>
          ) : null}

          {tx.counterparty && tx.description ? (
            <span className="truncate">{tx.counterparty.name}</span>
          ) : null}

          {tx.kind === 'EXCHANGE' && tx.fxRate ? (
            <span className="tabular">курс {formatRate(tx.fxRate)}</span>
          ) : null}
        </div>

        {tx.voidReason ? (
          <p className="mt-1 text-xs italic text-muted-foreground">{tx.voidReason}</p>
        ) : null}
      </div>

      <div className="flex shrink-0 items-start gap-1">
        <div className="text-right">
          {twoSided && outLeg && inLeg ? (
            outLeg.currency === inLeg.currency ? (
              <Money
                minor={Math.abs(outLeg.amountMinor)}
                currency={outLeg.currency}
                tone="neutral"
                className="font-medium"
              />
            ) : (
              <div className="space-y-0.5">
                <Money
                  minor={outLeg.amountMinor}
                  currency={outLeg.currency}
                  tone="neutral"
                  signed
                  className="block text-sm"
                />
                <Money
                  minor={inLeg.amountMinor}
                  currency={inLeg.currency}
                  tone="neutral"
                  signed
                  className="block text-sm"
                />
              </div>
            )
          ) : single ? (
            <Money
              minor={single.amountMinor}
              currency={single.currency}
              tone={toneForKind(tx.kind)}
              signed
              className={cn('font-medium', voided && 'line-through')}
            />
          ) : null}
        </div>

        {onVoid && !voided && !isReversal ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm" aria-label="Действия">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem destructive onSelect={() => onVoid(tx)}>
                <Undo2 />
                Сторнирай
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    </div>
  );
}
